import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { Pagination, TablePagination, TableSortLabel } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import BrandOverviewTableSkeleton from "../../../BrandSkeletons/BrandOverviewTableSkeleton";
import { Stack } from "@mui/system";

interface Props {
  tableHeadings?: { [key: string]: string };
}

const tableHeaders = [
  {
    id: 1,
    KEY: "raw_output_table_name",
    label: "Raw Output Table",
    sortable: true,
  },
  {
    id: 2,
    KEY: "config_name",
    label: "Config Name",
    sortable: true,
  },
  {
    id: 3,
    KEY: "report_name",

    label: "Report Name",
    sortable: true,
  },
  {
    id: 4,
    KEY: "format_type",
    label: "Format Type",
    sortable: true,
  },
  {
    id: 5,
    KEY: "collection_method_name",
    label: "Collection Method",
    sortable: true,
  },
  {
    id: 6,
    KEY: "collection_type_name",
    label: "Collection Type",
    sortable: true,
  },
  {
    id: 7,
    KEY: "sub_filtering_method_name",
    label: "Supports Filtering Based on",
    sortable: true,
  },
];

const FetcherSettingsTable = ({ tableHeadings }: Props) => {
  const [searchParams] = useSearchParams("");
  const [sortedData, setSortedData] = useState<any[]>([]);
  const [sort, setSort] = useState<{
    keytoSort: string;
    direction: "asc" | "desc";
  }>({ keytoSort: "raw_output_table_name", direction: "desc" });

  const headCells = Object.entries(tableHeadings || {});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const loaderForSwitchingTabs = useSelector(
    (state: any) => state.brandConfigurationReport.loaderForSwitchingTabs,
  );

  const reportType = searchParams.get("report") || "daily_user";

  const brandOverviewFetcherSettingsData = useSelector(
    (state: any) =>
      state.brandConfigurationReport.brandOverviewFetcherSettingsData[
        reportType
      ],
  );

  //-- Sorting Data
  const getSortedData = useCallback(
    (arrayToSort: any[]) => {
      // If no sort is applied, return original order
      if (!sort.keytoSort || !sort.direction) {
        return [...arrayToSort]; // Return a copy of original order
      }

      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sort.keytoSort;

        if (keytoSort === undefined || keytoSort === null) return 0;

        let aValue;
        let bValue;

        aValue = a[keytoSort];
        bValue = b[keytoSort];

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;
        if (aValue === "") return 1;
        if (bValue === "") return -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        } else {
          return sort.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    [sort],
  );

  useEffect(() => {
    if (
      !brandOverviewFetcherSettingsData ||
      brandOverviewFetcherSettingsData.length === 0
    )
      return;

    // Then sort the data with accurate counts
    const sortedRows = getSortedData(brandOverviewFetcherSettingsData || []);

    setSortedData(sortedRows);
  }, [brandOverviewFetcherSettingsData, getSortedData]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedData.slice(start, start + rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  //-- Function to handle click on table headers for sorting
  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable

    setSort({
      keytoSort: header.KEY,
      // Toggle sort direction if the same column is clicked, otherwise default to ascending
      direction:
        header.KEY === sort.keytoSort && sort.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  return (
    <>
      {loaderForSwitchingTabs ? (
        <>
          <BrandOverviewTableSkeleton />
        </>
      ) : (
        <>
          <TableContainer
            component={Paper}
            className="performanceTable"
            sx={{
              padding: "0px 0px",
              boxShadow: "none",
              borderStartStartRadius: "0px",
              maxHeight: 450,
              overflowX: "auto",
            }}
          >
            <Table aria-label="collapsible table" size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {tableHeaders.map((header) => (
                    <TableCell key={header.id} align="left">
                      <TableSortLabel
                        active={sort.keytoSort === header.KEY}
                        direction={
                          sort.keytoSort === header.KEY ? sort.direction : "asc"
                        }
                        onClick={() => handleHeaderClick(header)}
                        disabled={!header.sortable}
                      >
                        {header.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows?.length > 0 ? (
                  paginatedRows.map((row: any, rowIndex: number) => (
                    <React.Fragment key={rowIndex}>
                      <TableRow
                        sx={{
                          "& > *": {
                            borderBottom:
                              "1px solid rgba(224, 224, 224, 1) !important",
                          },
                        }}
                      >
                        {headCells.map(([key], cellIndex) => (
                          <TableCell key={cellIndex} align="left">
                            {key === "raw_output_table_name" ? (
                              <>
                                <Link
                                  style={{ color: "#9C27B0" }}
                                  to="#"
                                  onClick={(e) => e.preventDefault()} //stops URL change
                                >
                                  {" "}
                                  {row[key] ? row[key] : ""}{" "}
                                </Link>
                              </>
                            ) : key === "config_name" ? (
                              <> {row[key] ? row[key] : ""}</>
                            ) : key === "report_name" ? (
                              <> {row[key] ? row[key] : ""}</>
                            ) : key === "format_type" ? (
                              <> {row[key] ? row[key] : ""}</>
                            ) : key === "collection_method_name" ? (
                              <> {row[key] ? row[key] : ""}</>
                            ) : key === "collection_type_name" ? (
                              <> {row[key] ? row[key] : ""}</>
                            ) : key === "sub_filtering_method_name" ? (
                              <> {row[key] ? row[key] : ""}</>
                            ) : (
                              ""
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    </React.Fragment>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={headCells.length} align="center">
                      No data found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {sortedData?.length > 0 && (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <TablePagination
                rowsPerPageOptions={[]}
                component="div"
                count={sortedData?.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                    {
                      display: "none",
                    },
                  ".MuiTablePagination-actions": {
                    display: "none",
                  },
                }}
              />
              <Pagination
                count={Math.ceil(sortedData?.length / rowsPerPage)}
                page={page + 1}
                onChange={(_, value) => handleChangePage(null, value - 1)}
                showFirstButton
                showLastButton
                className="pagination-button"
                color="secondary"
                size="small"
                sx={{
                  ".css-wjh20t-MuiPagination-ul": {
                    justifyContent: "center",
                  },
                }}
              />
              <TablePagination
                rowsPerPageOptions={[10, 25, 100]}
                component="div"
                count={sortedData?.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                classes={{
                  displayedRows: "hidden-displayed-rows", // Apply the custom class
                }}
                sx={{
                  ".MuiTablePagination-actions": {
                    display: "none",
                  },
                  ".MuiTablePagination-input": {
                    marginRight: "15px",
                  },
                }}
              />
            </Stack>
          )}
        </>
      )}
    </>
  );
};

export default FetcherSettingsTable;
